import { Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { Customer } from "../../../../shared/types/customers";
import { AppType } from "../app.type";
import { getApp } from "../app";
import { WhereUsedTreeNode } from "./where-used-tree.model";
import { WhereUsedTreeService } from "./where-used-tree.service";

@Component({
    selector: "app-where-used-tree",
    templateUrl: "./where-used-tree.component.html",
    styleUrls: ["./where-used-tree.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class WhereUsedTreeComponent implements OnChanges {
  @Input() customer = "";
  @Input() partNumber = "";
  @Input() maxDepth = 25;

  loading = false;
  selectedPartNumber = "";
  selectedDesignation = "";
  roots: WhereUsedTreeNode[] = [];
  rows: WhereUsedTreeNode[] = [];
  uniqueParentItems = 0;
  totalUsages = 0;
  columns: string[] = [];

  app = getApp((app: AppType) => {
    this.app = app;
    this.getColumns();
  });

  constructor(
    private modalService: NgbModal,
    private whereUsedTree: WhereUsedTreeService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    this.getColumns();

    if (
      changes.customer != null ||
      changes.partNumber != null ||
      changes.maxDepth != null
    ) {
      this.load();
    }
  }

  async load() {
    if (this.customer === "" || this.partNumber === "") {
      this.clear();
      return;
    }

    this.loading = true;

    try {
      const result = await this.whereUsedTree.getWhereUsed(
        this.customer,
        this.partNumber,
        this.maxDepth
      );

      this.selectedPartNumber = result.partNumber;
      this.selectedDesignation = result.designation;
      this.roots = this.whereUsedTree.buildTree(result.rows);
      this.uniqueParentItems = new Set(
        this.roots.map((row) => row.partNumber)
      ).size;
      this.totalUsages = this.getTotalUsageQuantity(this.roots);
      this.rows = this.whereUsedTree.flattenOpenNodes(this.roots);
    } catch (e) {
      console.log(e);
      this.clear();
    }

    this.loading = false;
  }

  toggle(node: WhereUsedTreeNode) {
    node.isOpen = !node.isOpen;
    this.rows = this.whereUsedTree.flattenOpenNodes(this.roots);
  }

  getLevel(node: WhereUsedTreeNode) {
    return Math.max(node.path.length - 1, 0);
  }

  getColumns() {
    if (this.app.overrideMpnFields.openOverrideModal) {
      this.columns = [this.app.fieldId.impact.artNumber];
    } else {
      this.columns = this.app.list.impact.RMClientColumns;
    }
  }

  get treeColumnsTemplate() {
    return this.columns
      .map((column) => this.getColumnWidth(column))
      .join(" ");
  }

  specialLogicLabels(id: string) {
    switch (id) {
      case "thread.artNumber":
        if (this.app.customers.expectCurrent === Customer.LRE) {
          return this.app.field.getLabel(this.app.fieldId.impact.artNumber);
        } else {
          return this.app.field.getLabel(id);
        }
      case "impact.omfVehicleCnt":
        if (this.app.customers.expectCurrent === Customer.NS) {
          return this.app.field.getLabel(this.app.fieldId.part.quantity);
        } else {
          return this.app.field.getLabel(id);
        }
      case "impact.omfVehicleName":
        if (this.app.customers.expectCurrent === Customer.NS) {
          return this.app.field.getLabel(this.app.fieldId.part.description);
        } else {
          return this.app.field.getLabel(id);
        }
      default:
        return this.app.field.getLabel(id);
    }
  }

  getColumnType(column: string) {
    return this.app.table.getColumnTypeDisplay(this.getColumnKey(column));
  }

  getColumnClass(column: string) {
    return `tree-column-${this.getColumnKey(column)}`;
  }

  getColumnValue(row: WhereUsedTreeNode, column: string) {
    const key = this.getColumnKey(column);

    switch (key) {
      case "artNumber":
        return row.partNumber;
      case "omfVehicleCnt":
        return row.quantity;
      case "omfVehicleName":
        return row.designation;
      default:
        return row[key] != null ? row[key] : "";
    }
  }

  getUniqueDirectParentItemsFooter() {
    return this.app.text.part.whereUsedShowingUniqueDirectParentItems
      .replace("$", String(this.uniqueParentItems))
      .replace("&", String(this.uniqueParentItems));
  }

  close() {
    this.modalService.dismissAll();
  }

  private clear() {
    this.selectedPartNumber = "";
    this.selectedDesignation = "";
    this.roots = [];
    this.rows = [];
    this.uniqueParentItems = 0;
    this.totalUsages = 0;
  }

  private getColumnKey(column: string) {
    return column.split(".")[1] || column;
  }

  private getTotalUsageQuantity(nodes: WhereUsedTreeNode[]): number {
    return nodes.reduce(
      (total, node) => total + this.getUsageQuantityForPath(node, 1),
      0
    );
  }

  private getUsageQuantityForPath(
    node: WhereUsedTreeNode,
    parentQuantity: number
  ): number {
    const pathQuantity = parentQuantity * this.getQuantityNumber(node.quantity);

    if (node.children.length === 0) {
      return pathQuantity;
    }

    return node.children.reduce(
      (total, child) => total + this.getUsageQuantityForPath(child, pathQuantity),
      0
    );
  }

  private getQuantityNumber(
    quantity: string | number | null | undefined
  ): number {
    if (quantity == null || quantity === "") {
      return 0;
    }

    const numberValue = Number(String(quantity).replace(",", "."));
    return Number.isFinite(numberValue) ? numberValue : 0;
  }

  private getColumnWidth(column: string) {
    switch (this.getColumnKey(column)) {
      case "artNumber":
        return "minmax(200px, 1.4fr)";
      case "omfVehicleCnt":
        return "minmax(80px, 0.5fr)";
      case "omfVehicleName":
        return "minmax(200px, 1.55fr)";
      default:
        return "minmax(100px, 0.9fr)";
    }
  }
}
